import { useCallback, useRef, useState } from 'react'

import { F0Box, F0Button, F0Dialog, F0Text } from '@factorialco/f0-react'
import { CardSelectableContainer, Input } from '@factorialco/f0-react/dist/experimental'
import { AlignTextJustify, Delete, TextSize } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { toCustomFieldType, type MergedField, type SectionType } from './types'

type FieldTypeOption = 'text' | 'select'

type Props = {
  section: SectionType
  /** When set, the modal opens in edit mode for this custom field. */
  editingField?: MergedField | null
  onClose: () => void
  onAdd: (
    section: SectionType,
    label: string,
    fieldType: FieldTypeOption,
    options: string[]
  ) => void
  onEdit?: (key: string, label: string, options: string[]) => void
}

type DropdownOption = { id: number; value: string }

function AddFieldModal({ section, editingField, onClose, onAdd, onEdit }: Props) {
  const isEditMode = Boolean(editingField)
  const initialFieldType: FieldTypeOption = editingField
    ? (toCustomFieldType(editingField.fieldType) ?? 'text')
    : 'text'
  const initialOptions: DropdownOption[] = editingField?.options.length
    ? editingField.options.map((value, index) => ({ id: index, value }))
    : [{ id: 0, value: '' }]

  const [fieldName, setFieldName] = useState(editingField?.displayLabel ?? '')
  const [fieldType, setFieldType] = useState<FieldTypeOption>(initialFieldType)
  const nextOptionId = useRef(initialOptions.length)
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>(initialOptions)

  const handleFieldTypeChange = useCallback((value: FieldTypeOption | undefined) => {
    if (value === 'text' || value === 'select') {
      setFieldType(value)
    }
  }, [])

  const handleOptionChange = useCallback((optionId: number, value: string) => {
    setDropdownOptions((prev) => prev.map((opt) => (opt.id === optionId ? { ...opt, value } : opt)))
  }, [])

  const handleDeleteOption = useCallback((optionId: number) => {
    setDropdownOptions((prev) => prev.filter((opt) => opt.id !== optionId))
  }, [])

  const handleAddOption = useCallback(() => {
    const id = nextOptionId.current
    nextOptionId.current += 1
    setDropdownOptions((prev) => [...prev, { id, value: '' }])
  }, [])

  const isValid =
    fieldName.trim().length > 0 &&
    (fieldType === 'text' || dropdownOptions.filter((o) => o.value.trim().length > 0).length >= 1)

  const handleSubmit = useCallback(() => {
    const trimmed = fieldName.trim()
    if (!trimmed) return
    const options =
      fieldType === 'select'
        ? dropdownOptions.map((o) => o.value.trim()).filter((o) => o.length > 0)
        : []
    if (isEditMode && editingField && onEdit) {
      onEdit(editingField.key, trimmed, options)
    } else {
      onAdd(section, trimmed, fieldType, options)
    }
    onClose()
  }, [
    fieldName,
    fieldType,
    dropdownOptions,
    section,
    onAdd,
    onEdit,
    onClose,
    isEditMode,
    editingField,
  ])

  const optionPlaceholder = (index: number) =>
    `${i18n.t('procurement.purchase_requests.settings.po_templates.detail.option_label')} ${index + 1}`

  const dialogTitle = isEditMode
    ? i18n.t('procurement.purchase_requests.settings.po_templates.detail.edit_field_title')
    : i18n.t('procurement.purchase_requests.settings.po_templates.detail.add_field_title')
  const primaryLabel = isEditMode ? i18n.t('common.save') : i18n.t('common.add')

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      width="sm"
      title={dialogTitle}
      primaryAction={{
        label: primaryLabel,
        onClick: handleSubmit,
        disabled: !isValid,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="lg">
        {/* Field type selector — hidden in edit mode (changing type for an existing UUID would orphan stored values) */}
        {!isEditMode && (
          <CardSelectableContainer<FieldTypeOption>
            layout="horizontal"
            value={fieldType}
            onChange={handleFieldTypeChange}
            items={[
              {
                value: 'text',
                title: i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.field_type_text'
                ),
                avatar: { type: 'icon', icon: TextSize },
              },
              {
                value: 'select',
                title: i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.field_type_select'
                ),
                avatar: { type: 'icon', icon: AlignTextJustify },
              },
            ]}
          />
        )}

        {/* Field name */}
        <Input
          label={i18n.t(
            'procurement.purchase_requests.settings.po_templates.detail.field_name_placeholder'
          )}
          value={fieldName}
          onChange={setFieldName}
          placeholder={i18n.t(
            'procurement.purchase_requests.settings.po_templates.detail.field_name_placeholder'
          )}
          required
          autoFocus
        />

        {/* Dropdown options editor (only for select type) */}
        {fieldType === 'select' && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text
              content={i18n.t(
                'procurement.purchase_requests.settings.po_templates.detail.dropdown_options'
              )}
              variant="body"
            />

            {dropdownOptions.map((option, index) => (
              <F0Box
                key={option.id}
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="md"
                paddingBottom="md"
              >
                <F0Box grow>
                  <Input
                    label={optionPlaceholder(index)}
                    hideLabel
                    value={option.value}
                    onChange={(value: string) => handleOptionChange(option.id, value)}
                    placeholder={optionPlaceholder(index)}
                  />
                </F0Box>
                <F0Button
                  icon={Delete}
                  label={i18n.t('common.delete')}
                  hideLabel
                  variant="outline"
                  size="md"
                  onClick={() => handleDeleteOption(option.id)}
                />
              </F0Box>
            ))}

            <F0Box alignItems="start" paddingTop="sm">
              <F0Button
                label={i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.new_option'
                )}
                variant="outline"
                size="md"
                onClick={handleAddOption}
              />
            </F0Box>
          </F0Box>
        )}
      </F0Box>
    </F0Dialog>
  )
}

export default AddFieldModal
